import { ScheduleControls } from 'components/AddonsMode/ScheduleControls/ScheduleControls';
import { RadioCard } from 'components/Form/RadioCard/RadioCard';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { AddonSchedule, AddonScheduleMode } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import { PortalBookingAddon } from 'types/PortalBooking';
import { LockedDays } from 'utils/addonsMode';
import { formatServicePriceLabel } from 'utils/serviceUtils';

import classes from '../AddonsMode.module.scss';

interface AddonCardProps {
    addon: AddonService;
    eligibleDates: Array<string>;
    locked: LockedDays;
    // Every line of this Add-On already on the booking for the pet, past days included.
    saved: Array<PortalBookingAddon>;
    schedule?: AddonSchedule;
    // Mark a Select Days with no day as an error (the pet parent tried to move on).
    showScheduleError?: boolean;
    onToggle: (checked: boolean) => void;
    onScheduleChange: (schedule: AddonSchedule) => void;
}

/**
 * One catalog Add-On: "+ Add" / "Selected" (Figma 1930:6145, 1930:6228). An Add-On already on the
 * booking starts selected and can't be unselected: its days show checked and disabled, and the
 * open ones can still be added.
 */
export const AddonCard: React.FC<AddonCardProps> = ({
    addon,
    eligibleDates,
    locked,
    saved,
    schedule,
    showScheduleError,
    onToggle,
    onScheduleChange
}) => {
    const isOnBooking = saved.some(line => line.date === null || eligibleDates.includes(line.date));
    const { onlineBookingPreferences } = useMarketplace();

    const hasScheduleError =
        showScheduleError &&
        schedule?.mode === AddonScheduleMode.SelectDays &&
        Object.keys(schedule.days).length === 0;

    const changeSchedule = (next: AddonSchedule | null) => {
        // On an Add-On already on the booking, no new day means no new selection, not an error.
        const isEmpty =
            !next ||
            (isOnBooking &&
                next.mode === AddonScheduleMode.SelectDays &&
                Object.keys(next.days).length === 0);

        if (isEmpty) {
            onToggle(false);
        } else {
            onScheduleChange(next);
        }
    };

    return (
        <RadioCard
            mode="checkbox"
            className={hasScheduleError ? classes.cardError : undefined}
            title={addon.name}
            description={formatServicePriceLabel(
                addon.price,
                onlineBookingPreferences.pricingOption
            )}
            checked={Boolean(schedule) || isOnBooking}
            checkedLabel="Selected"
            locked={isOnBooking}
            lockedLabel={`${addon.name} is already on this booking`}
            onChange={onToggle}
        >
            {(schedule || isOnBooking) && (
                <ScheduleControls
                    addonName={addon.name}
                    eligibleDates={eligibleDates}
                    locked={locked}
                    value={schedule}
                    showError={hasScheduleError}
                    onChange={changeSchedule}
                />
            )}
        </RadioCard>
    );
};
